import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent, Chip, Typography, Box, IconButton, Avatar } from '@mui/material';
import { Edit as EditIcon, Schedule as ScheduleIcon, CheckCircle as CheckCircleIcon, PlayArrow as PlayArrowIcon, LocalShipping as LocalShippingIcon, Description as DescriptionIcon, Done as DoneIcon } from '@mui/icons-material';
import FormattedShift from '../../models/FormattedShift';
import { formatDateToOnlyDate, formatDateToOnlyTime, isDriver, getJobSiteName } from '../../utils/common';
import LocalStorageService from '../../services/LocalStorageService';

interface Props {
  shift: any;
  // 'scheduled' | 'ongoing' | 'completed'
  shiftType: string;
  // Optional: replace the action area on the right of the header
  actionNode?: React.ReactNode;
  // Optional: extra content rendered at the bottom of the card content
  footer?: React.ReactNode;
}

const MobileShiftCard: React.FC<Props> = ({ shift, shiftType, actionNode, footer }) => {
  const navigate = useNavigate();
  const localStorageService = LocalStorageService.getService();
  const roles = localStorageService.getUserRoles();

  const onRowClick = () => {
    if (isDriver(roles)) {
      if ((Number(shift.shiftStatus) === 1 || Number(shift.shiftStatus) === 2) && shift.extCompany?.companyId) {
        navigate(`/employee/shift/${shift.id}/submit`);
      } else {
        navigate(`/employee/shift/${shift.id}`);
      }
    } else {
      navigate(`/app/shift/list/${shift.id}`);
    }
  };

  const getStatusChip = () => {
    const statusConfig: { [key: string]: { label: string; color: string; icon: React.ReactElement } } = {
      ongoing: {
        label: 'Ongoing',
        color: '#FF9800',
        icon: <PlayArrowIcon fontSize="small" />
      },
      scheduled: {
        label: 'Scheduled',
        color: '#2196F3',
        icon: <ScheduleIcon fontSize="small" />
      },
      completed: {
        label: 'Completed',
        color: '#4CAF50',
        icon: <CheckCircleIcon fontSize="small" />
      }
    };

    const config = statusConfig[shiftType] || statusConfig.scheduled;

    return (
      <Chip
        icon={React.cloneElement(config.icon, { fontSize: 'inherit' })}
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: 'white',
          fontWeight: 500,
          fontSize: '0.65rem',
          height: 22,
          px: 0.6,
          '& .MuiChip-icon': {
            color: 'white',
            fontSize: '1rem'
          }
        }}
      />
    );
  };

  const getAcceptanceStatus = () => {
    if (shift.userHasAccepted === true) return { label: 'Accepted', color: '#4CAF50' };
    if (shift.userHasAccepted === false) return { label: 'Rejected', color: '#F44336' };
    return { label: 'Pending', color: '#FF9800' };
  };

  const acceptanceStatus = getAcceptanceStatus();

  // compute accent background to avoid nested ternary in JSX (ESLint: no-nested-ternary)
  let accentBackground = 'linear-gradient(180deg,#42a5f5,#1e88e5)';
  if (shiftType === 'ongoing') {
    accentBackground = 'linear-gradient(180deg,#ffb74d,#ff9800)';
  } else if (shiftType === 'completed') {
    accentBackground = 'linear-gradient(180deg,#66bb6a,#43a047)';
  }

  return (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'visible',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(250,250,252,0.85))',
        boxShadow: '0 6px 20px rgba(16,24,40,0.08)',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 12px 30px rgba(16,24,40,0.12)'
        },
        position: 'relative',
        p: 0
      }}
      variant="outlined"
      onClick={onRowClick}
    >
      {/* Accent strip on the left */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 12,
          bottom: 12,
          width: 6,
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
          background: accentBackground
        }}
      />

      <CardHeader
        // avatar={(
        //   <Avatar sx={{ bgcolor: getStatusChip().props.sx.backgroundColor, width: 36, height: 36, fontWeight: 600, fontSize: '0.95rem' }}>
        //     {shift.userName ? shift.userName.charAt(0).toUpperCase() : 'U'}
        //   </Avatar>
        // )}
        action={actionNode ?? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* status chip with subtle glass */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>{getStatusChip()}</Box>
            </Box>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRowClick(); }} sx={{ ml: 0.5 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        title={(
          <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '0.2px' }}>
            {shift.userName || shift.userEmail || 'Unknown Driver'}
          </Typography>
        )}
        subheader={(
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatDateToOnlyDate(shift.scheduledStartUTC, true)} • {formatDateToOnlyTime(shift.scheduledStartUTC, true)}
          </Typography>
        )}
        sx={{ pb: 1, pt: 1, pl: 3, position: 'relative' }}
      />

      <CardContent sx={{ pt: 0, pb: 2, pl: 3, pr: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Store/Jobsite */}
          {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {getJobSiteName(shift.jobsite)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ background: 'rgba(0,0,0,0.03)', px: 1, borderRadius: 1 }}>
              {shift.amOrPm || '-'}
            </Typography>
          </Box> */}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Vehicle */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }} aria-hidden>
                <LocalShippingIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Box component="span">{shift.vehicleRego || `${shift.unlistedVehicleRego} (Not listed)`}</Box>
              </Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }} aria-hidden>
                <DescriptionIcon fontSize="small" />
                <Box component="span">{getJobSiteName(shift.jobsite)}</Box>
              </Box>
            </Typography>

            {/* Timesheet status for completed shifts */}
            {/* {shiftType === 'completed' && shift.timesheetStatus && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DescriptionIcon fontSize="small" />
                <Box component="span">Timesheet: {shift.timesheetStatus}</Box>
              </Typography>
            )} */}

            {/* Check-in status */}
            {/* {shift.checkinStatus && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DoneIcon fontSize="small" />
                <Box component="span">Check-in: {shift.checkinStatus}</Box>
              </Typography>
            )} */}
          </Box>

          {/* Acceptance Status for scheduled shifts */}
          {/* {shiftType === 'scheduled' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Status:
              </Typography>
              <Chip
                label={acceptanceStatus.label}
                size="small"
                sx={{
                  backgroundColor: `${acceptanceStatus.color}18`,
                  color: acceptanceStatus.color,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 24,
                  borderRadius: 1
                }}
              />
            </Box>
          )} */}
          {footer && (
            <Box sx={{ mt: 1.5 }}>
              {footer}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MobileShiftCard;
